import React from 'react';
import { addLeave as dbAddLeave } from '../services/db';
import { getHeadEmailForDepartment, normalizeHeadDepartmentsFromUser } from '../services/userDb';

function inclusiveCalendarDays(fromStr, toStr) {
  const from = new Date(`${fromStr}T12:00:00`);
  const to = new Date(`${toStr}T12:00:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return 0;
  return Math.round((to - from) / 864e5) + 1;
}

function formatLeaveDaysCell(leave) {
  const d = leave.days;
  if (d == null || Number.isNaN(Number(d))) return '—';
  const n = Number(d);
  const base = Number.isInteger(n) ? String(n) : (Math.round(n * 10) / 10).toString();
  const parts = [];
  if (leave.leaveHalfSession === 'am') parts.push('AM');
  else if (leave.leaveHalfSession === 'pm') parts.push('PM');
  if (leave.leaveHalfFirst) parts.push('1st ½');
  if (leave.leaveHalfLast) parts.push('last ½');
  return parts.length ? `${base} (${parts.join(', ')})` : base;
}

export default function MyLeaves({ currentUser, leaves, showToast, openModal }) {
  // Match by email natively
  const myLeaves = leaves.filter(l => l.eEmail === currentUser.email || l.emp === currentUser.name);

  const handleApply = () => {
    openModal({
      isOpen: true,
      title: 'Apply for Leave',
      content: (
        <>
          <div className="fg">
            <label>Leave Type *</label>
            <select id="mlt">
              <option>Casual Leave</option>
              <option>Sick Leave</option>
              <option>Earned Leave</option>
              <option>Unpaid Leave</option>
            </select>
          </div>
          <div className="fr">
            <div className="fg">
              <label>From Date *</label>
              <input id="mlf" type="date" />
            </div>
            <div className="fg">
              <label>To Date *</label>
              <input id="mlt2" type="date" />
            </div>
          </div>
          <div className="fg">
            <label>Duration (same start &amp; end date) *</label>
            <select id="ml-single" defaultValue="full">
              <option value="full">Full day</option>
              <option value="half-am">Half day — morning (AM)</option>
              <option value="half-pm">Half day — afternoon (PM)</option>
            </select>
            <div style={{ fontSize: '12px', color: 'var(--text2)', marginTop: '6px' }}>
              Used when From and To are the same calendar day.
            </div>
          </div>
          <div className="fg" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ marginBottom: 0 }}>Multi-day partial days</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500, cursor: 'pointer' }}>
              <input id="ml-hfirst" type="checkbox" />
              First day is a half day (−0.5)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500, cursor: 'pointer' }}>
              <input id="ml-hlast" type="checkbox" />
              Last day is a half day (−0.5)
            </label>
            <div style={{ fontSize: '12px', color: 'var(--text2)' }}>
              Used when From and To are different dates (e.g. Mon–Wed with last day half = 2.5 days).
            </div>
          </div>
          <div className="fg">
            <label>Reason *</label>
            <textarea id="mlr" rows="3" placeholder="Explain why you need this leave..."></textarea>
          </div>
        </>
      ),
      footer: (
        <>
          <button className="btn btn-g" onClick={() => openModal({ isOpen: false })}>Cancel</button>
          <button className="btn btn-p" onClick={submitLeave}>Submit Request</button>
        </>
      )
    });
  };

  const submitLeave = async () => {
    const from = document.getElementById('mlf')?.value;
    const to = document.getElementById('mlt2')?.value;
    const reason = document.getElementById('mlr')?.value;
    const type = document.getElementById('mlt')?.value;

    if (!from || !to || !reason) {
      showToast('Please fill all required fields', 'err');
      return;
    }

    if (new Date(from) > new Date(to)) {
      showToast('From date cannot be after To date', 'err');
      return;
    }

    const inclusive = inclusiveCalendarDays(from, to);
    if (inclusive < 1) {
      showToast('Invalid date range', 'err');
      return;
    }

    const singleMode = document.getElementById('ml-single')?.value || 'full';
    const halfFirst = !!document.getElementById('ml-hfirst')?.checked;
    const halfLast = !!document.getElementById('ml-hlast')?.checked;

    let days;
    let leaveHalfSession;
    let leaveHalfFirst = false;
    let leaveHalfLast = false;

    if (from === to) {
      if (singleMode === 'half-am') {
        days = 0.5;
        leaveHalfSession = 'am';
      } else if (singleMode === 'half-pm') {
        days = 0.5;
        leaveHalfSession = 'pm';
      } else {
        days = 1;
      }
    } else {
      days = inclusive - (halfFirst ? 0.5 : 0) - (halfLast ? 0.5 : 0);
      leaveHalfFirst = halfFirst;
      leaveHalfLast = halfLast;
    }

    if (days < 0.5) {
      showToast('That combination is not valid (days must be at least 0.5)', 'err');
      return;
    }

    const empDept = String(currentUser.department || currentUser.dept || '').trim();

    try {
      openModal({ isOpen: false });
      showToast('Sending leave request...', 'inf');

      let headEmail = null;
      if (empDept && empDept !== 'General' && empDept !== 'HR') {
        headEmail = await getHeadEmailForDepartment(empDept);
      }
      // Skip "send to head" if this employee is the assigned head for their own department (goes to HR instead).
      const headsICover = normalizeHeadDepartmentsFromUser(currentUser);
      const deptLower = empDept.toLowerCase();
      const selfIsHead =
        (headEmail && headEmail === currentUser.email) ||
        headsICover.some((h) => String(h).trim().toLowerCase() === deptLower);
      const useHead = headEmail && !selfIsHead;

      const payload = {
        eEmail: currentUser.email,
        emp: currentUser.name,
        ini: currentUser.ini,
        type,
        from,
        to,
        days,
        reason,
        empDept: empDept || '',
        applied: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(),
        ...(leaveHalfSession ? { leaveHalfSession } : {}),
        ...(leaveHalfFirst ? { leaveHalfFirst: true } : {}),
        ...(leaveHalfLast ? { leaveHalfLast: true } : {}),
      };

      if (useHead) {
        payload.status = 'pending_head';
        payload.headEmail = headEmail;
      } else {
        payload.status = 'pending';
        if (headEmail) payload.headEmail = null;
      }

      await dbAddLeave(payload);

      showToast(
        useHead ? 'Leave request sent to your department head for approval.' : 'Leave request submitted to HR.',
        'ok'
      );
    } catch (e) {
      showToast('Failed to submit leave request', 'err');
    }
  };

  return (
    <div className="pg">
      <div className="ph" style={{ marginBottom: '20px' }}>
        <h2>My Leave Requests</h2>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
          <div style={{ padding: '8px 12px', background: 'var(--bg-sec)', borderRadius: '6px', fontSize: '13px', fontWeight: '500' }}>
            Casual Leaves Left: <strong style={{ color: 'var(--p)' }}>{currentUser.leaveBalance?.casual || 0}</strong>
          </div>
          <button 
            className="btn btn-p" 
            onClick={handleApply}
          >
            ➕ Apply for Leave
          </button>
        </div>
      </div>
      
      <div className="card" style={{ padding: '0' }}>
        {myLeaves.length === 0 ? (
          <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text2)' }}>No leave requests found.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-sec)', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)' }}>TYPE</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)' }}>DATES</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)' }}>DAYS</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)' }}>REASON</th>
                <th style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text2)' }}>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {myLeaves.map(l => (
                <tr key={l.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '12px 16px', fontWeight: '500' }}>{l.type}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px' }}>{l.from} to {l.to}</td>
                  <td style={{ padding: '12px 16px', fontWeight: '500' }}>{formatLeaveDaysCell(l)}</td>
                  <td style={{ padding: '12px 16px', fontSize: '13px', color: 'var(--text2)' }}>{l.reason}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ 
                      padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                      background: l.status === 'approved' ? '#edfaf2' : l.status === 'rejected' ? '#fef2f2' : '#fffbeb',
                      color: l.status === 'approved' ? '#2a9d4f' : l.status === 'rejected' ? '#c0392b' : '#d97706'
                    }}>
                      {l.status === 'pending_head' ? 'Pending (head)' : l.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
